import { Link } from 'react-router-dom';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import {
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from '../components_catalyst/dropdown';
import { Button } from '../components_catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components_catalyst/table';

export interface MapListItem {
  shortId: string;
  title: string;
  ownerName: string;
  starred: boolean;
}

interface MapListTableProps {
  maps: MapListItem[];
  onStar: (shortId: string) => void;
  onDelete?: (shortId: string) => void;
  onCopyLink: (shortId: string, ownerName: string) => void;
  showOwnerColumn?: boolean;
  showDelete?: boolean;
}

/**
 * Table component for displaying maps in list view.
 * Provides Google Drive-like UX for viewing map listings.
 */
export function MapListTable({
  maps,
  onStar,
  onDelete,
  onCopyLink,
  showOwnerColumn = false,
  showDelete = true,
}: MapListTableProps) {
  return (
    <Table striped>
      <TableHead>
        <TableRow>
          <TableHeader className="w-8">
            <span className="sr-only">Starred</span>
          </TableHeader>
          <TableHeader>Title</TableHeader>
          {showOwnerColumn && <TableHeader>Owner</TableHeader>}
          <TableHeader className="w-12">
            <span className="sr-only">Actions</span>
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {maps.map((map) => (
          <TableRow key={map.shortId}>
            {/* Star column */}
            <TableCell>
              <Button
                plain
                onClick={() => onStar(map.shortId)}
                aria-label={map.starred ? 'Remove star' : 'Star map'}
                aria-pressed={map.starred}
                className="p-0"
              >
                {map.starred ? (
                  <StarIconSolid
                    className="size-5 text-yellow-400"
                    aria-hidden="true"
                  />
                ) : (
                  <StarIcon
                    className="size-5 text-zinc-400"
                    aria-hidden="true"
                  />
                )}
              </Button>
            </TableCell>

            {/* Title column */}
            <TableCell>
              <Link
                to={`/${map.ownerName}/maps/${map.shortId}`}
                className="font-medium text-zinc-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
              >
                {map.title}
              </Link>
            </TableCell>

            {/* Owner column (conditional) */}
            {showOwnerColumn && (
              <TableCell className="text-zinc-500 dark:text-zinc-400">
                {map.ownerName}
              </TableCell>
            )}

            {/* Actions column */}
            <TableCell>
              <Dropdown>
                <DropdownButton as={Button} plain aria-label="Map options">
                  <EllipsisHorizontalIcon
                    className="size-5 text-zinc-500"
                    aria-hidden="true"
                  />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" aria-label="Map options menu">
                  <DropdownItem onClick={() => onCopyLink(map.shortId, map.ownerName)}>
                    <LinkIcon className="size-5" aria-hidden="true" />
                    Copy link
                  </DropdownItem>
                  <DropdownItem onClick={() => onStar(map.shortId)}>
                    {map.starred ? (
                      <StarIconSolid
                        className="size-5 text-yellow-400"
                        aria-hidden="true"
                      />
                    ) : (
                      <StarIcon className="size-5" aria-hidden="true" />
                    )}
                    {map.starred ? 'Unstar' : 'Star'}
                  </DropdownItem>
                  <DropdownItem
                    href={`/${map.ownerName}/maps/${map.shortId}`}
                  >
                    <ArrowTopRightOnSquareIcon
                      className="size-5"
                      aria-hidden="true"
                    />
                    View
                  </DropdownItem>
                  {showDelete && onDelete && (
                    <DropdownItem onClick={() => onDelete(map.shortId)}>
                      <TrashIcon className="size-5" aria-hidden="true" />
                      Delete
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </Dropdown>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
